import { useState, type Dispatch, type SetStateAction } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import {
  approveWorker,
  deleteWorker,
  listAllWorkers,
  suspendWorker,
} from "@/features/ops/api/opsApi";
import { useOpsStore } from "@/features/ops/model/opsStore";
import type { WorkerRow, WorkerStatus } from "@/features/ops/types";
import { WorkerDetailModal } from "@/features/ops/ui/WorkerDetailModal";
import { cn } from "@/shared/lib/cn";
import { Badge, Button, ConfirmDialog, type BadgeProps } from "@/shared/ui";

const STATUS_TABS: Array<{ label: string; value: WorkerStatus | undefined }> = [
  { label: "All", value: undefined },
  { label: "Pending", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Suspended", value: "SUSPENDED" },
];

const STATUS_VARIANT: Record<WorkerStatus, BadgeProps["variant"]> = {
  PENDING: "warning",
  APPROVED: "success",
  SUSPENDED: "warning",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function WorkerList() {
  const token = useOpsStore((s) => s.token)!;
  const qc = useQueryClient();
  const [status, setStatus] = useState<WorkerStatus | undefined>(undefined);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops", "workers", status ?? "all"],
    queryFn: () => listAllWorkers(token, status),
  });

  const [confirmDelete, setConfirmDelete] = useState<WorkerRow | null>(null);
  const [detailWorkerId, setDetailWorkerId] = useState<string | null>(null);

  const onWorkerStatusChange = () => {
    // A worker's status drives gating across every actor: the worker's own
    // dashboard, the venue's applicant list / favourites, and the other ops
    // queues. Invalidate every key that reads worker.status.
    qc.invalidateQueries({ queryKey: ["ops", "workers"] });
    qc.invalidateQueries({ queryKey: ["ops", "pending-workers"] });
    qc.invalidateQueries({ queryKey: ["ops", "worker-detail"] });
    qc.invalidateQueries({ queryKey: ["applicants"] });
    qc.invalidateQueries({ queryKey: ["favourites", "mine"] });
    qc.invalidateQueries({ queryKey: ["worker", "me"] });
  };

  const approve = useMutation({
    mutationFn: (workerId: string) => approveWorker(token, workerId),
    onSuccess: onWorkerStatusChange,
  });
  const suspend = useMutation({
    mutationFn: (workerId: string) => suspendWorker(token, workerId),
    onSuccess: onWorkerStatusChange,
  });
  const remove = useMutation({
    mutationFn: (workerId: string) => deleteWorker(token, workerId),
    onSuccess: () => {
      setConfirmDelete(null);
      // The worker (and any shifts/applications/ratings tied to them) are gone
      // — refresh every ops surface that could reference them.
      onWorkerStatusChange();
      qc.invalidateQueries({ queryKey: ["ops", "upcoming-shifts"] });
      qc.invalidateQueries({ queryKey: ["ops", "payouts"] });
    },
  });

  // Per-row pending state — clicking on one row no longer puts every other
  // row's button into the disabled/loading state.
  const [pendingApproveIds, setPendingApproveIds] = useState<Set<string>>(new Set());
  const [pendingSuspendIds, setPendingSuspendIds] = useState<Set<string>>(new Set());

  const trackPending = (
    setter: Dispatch<SetStateAction<Set<string>>>,
    id: string,
  ) => {
    setter((s) => new Set(s).add(id));
    return {
      onSettled: () =>
        setter((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        }),
    };
  };

  if (isLoading) return <div className="text-sm text-copy-muted">Loading…</div>;
  if (error) return <div className="text-sm text-destructive">Could not load workers.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-display-tight text-copy">All workers</h2>
        <div className="flex gap-1.5 overflow-x-auto rounded-md border border-border bg-surface-sunken p-0.5">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.label}
              onClick={() => setStatus(tab.value)}
              className={cn(
                "whitespace-nowrap rounded-[5px] px-2.5 py-1 text-xs font-medium transition-all",
                status === tab.value
                  ? "bg-surface text-copy shadow-card"
                  : "text-copy-muted hover:text-copy",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {data && data.length === 0 && (
        <div className="rounded-lg border border-border bg-surface p-10 text-center text-sm text-copy-muted shadow-card">
          No workers in this view.
        </div>
      )}

      <div className="space-y-2">
        {data?.map((w) => (
          <div
            key={w.id}
            onClick={() => setDetailWorkerId(w.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target === e.currentTarget) {
                setDetailWorkerId(w.id);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`View profile of ${w.name || w.email}`}
            className="cursor-pointer rounded-lg border border-border bg-surface p-4 shadow-card transition-colors hover:border-border-strong focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border border-border bg-surface-sunken">
                {w.photo_url ? (
                  <img src={w.photo_url} alt={w.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-copy-subtle">
                    —
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-copy">{w.name || "(no name)"}</p>
                  <Badge variant={STATUS_VARIANT[w.status]} dot>
                    {w.status}
                  </Badge>
                </div>
                <p className="truncate text-xs text-copy-muted">{w.email}</p>
                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-copy-muted">
                  {w.city && <span>{w.city}</span>}
                  <span>
                    <span className="text-warning">★</span>{" "}
                    {w.rating_avg != null ? w.rating_avg.toFixed(1) : "—"}
                  </span>
                  <span>{w.shift_count} shifts</span>
                  <span>Joined {fmtDate(w.created_at)}</span>
                  {w.approved_at && <span>Approved {fmtDate(w.approved_at)}</span>}
                  {w.certifications.length > 0 && (
                    <a
                      href={w.certifications[0].url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-primary hover:underline"
                      title={
                        w.certifications.length > 1
                          ? `${w.certifications.length} certifications on file`
                          : w.certifications[0].name
                      }
                    >
                      {w.certifications[0].name}
                      {w.certifications.length > 1
                        ? ` +${w.certifications.length - 1}`
                        : ""}
                    </a>
                  )}
                </div>
              </div>
              {/* Row click opens the profile — keep action clicks from bubbling. */}
              <div className="flex shrink-0 gap-1.5" onClick={(e) => e.stopPropagation()}>
                {w.status === "APPROVED" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      suspend.mutate(w.id, trackPending(setPendingSuspendIds, w.id))
                    }
                    disabled={pendingSuspendIds.has(w.id)}
                  >
                    Suspend
                  </Button>
                )}
                {w.status === "SUSPENDED" && (
                  <Button
                    size="sm"
                    onClick={() =>
                      approve.mutate(w.id, trackPending(setPendingApproveIds, w.id))
                    }
                    disabled={pendingApproveIds.has(w.id)}
                  >
                    Reinstate
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDelete(w)}
                  className="text-destructive-foreground hover:bg-destructive-light"
                  aria-label={`Delete ${w.name || w.email}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {detailWorkerId && (
        <WorkerDetailModal
          workerId={detailWorkerId}
          onClose={() => setDetailWorkerId(null)}
        />
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        destructive
        title="Delete this worker?"
        description={
          <>
            This permanently deletes{" "}
            <span className="font-medium text-copy">
              {confirmDelete?.name || confirmDelete?.email}
            </span>{" "}
            and all of their data — profile, applications, shift history,
            ratings, and certifications. This cannot be undone.
          </>
        }
        confirmLabel="Delete permanently"
        loadingLabel="Deleting…"
        loading={remove.isPending}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
